import React, { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '../services/supabaseClient';

const AuthContext = createContext({});

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null); // fast auth user
  const [profile, setProfile] = useState(null); // background user row
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(null); // 🔑 store JWT access token

  useEffect(() => {
    const initUser = async () => {
      const {
        data: { session },
        error,
      } = await supabase.auth.getSession();

      if (error || !session?.user) {
        setUser(null);
        setToken(null);
        setLoading(false);
        return;
      }

      const authUser = session.user;

      setUser({
        id: authUser.id,
        email: authUser.email,
        name: authUser.user_metadata?.name || '',
//        isSetupComplete: authUser.user_metadata?.isSetupComplete ?? false,
      });

      setToken(session.access_token); // 🔑 capture token

      // background load user row (non-blocking)
      loadProfile(authUser.id);

      setLoading(false);
    };

    const loadProfile = async (authId) => {
      const { data, error } = await supabase
        .from('users')
        .select('account_setup_complete, status')
        .eq('auth_id', authId)
        .single();

      if (error) {
        console.error('❌ Failed to fetch user profile:', error.message);
        return;
      }

      setProfile({
        accountSetupComplete: data?.account_setup_complete ?? false,
        status: data?.status ?? null,
      });
    };

    initUser();

    const { data: listener } = supabase.auth.onAuthStateChange((_event, session) => {
      if (!session?.user) {
        setUser(null);
        setProfile(null);
        setToken(null);
      } else {
        setUser({
          id: session.user.id,
          email: session.user.email,
          name: session.user.user_metadata?.name || '',
//          isSetupComplete: session.user.user_metadata?.isSetupComplete ?? false,
        });
        setToken(session.access_token); // 🔑 refresh token on change
        loadProfile(session.user.id);
      }
    });

    return () => {
      listener?.subscription?.unsubscribe();
    };
  }, []);

  // 🕒 Auto-logout after 3 hours of inactivity (stable version)
  useEffect(() => {
    const MAX_INACTIVITY = 3 * 60 * 60 * 1000;
    let inactivityTimer;

    const resetTimer = () => {
      clearTimeout(inactivityTimer);
      inactivityTimer = window.setTimeout(async () => {
        console.warn('⏰ Session expired due to inactivity.');
        await supabase.auth.signOut();
        window.location.href = '/';
      }, MAX_INACTIVITY);
    };

    // ✅ attach listeners once only
    const events = ['click', 'mousemove', 'keypress', 'scroll', 'touchstart'];
    events.forEach((evt) => document.addEventListener(evt, resetTimer));

    resetTimer(); // start timer when app first mounts

    // ✅ cleanup only on unmount (never during re-renders)
    return () => {
      clearTimeout(inactivityTimer);
      events.forEach((evt) => document.removeEventListener(evt, resetTimer));
    };
    // 👇 empty dependency array ensures one-time mount only
  }, []);

  const register = async (email, password, name) => {
    try {
      console.log('🚀 Starting registration for:', email);

      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: { data: { name } },
      });

      console.log('📧 Supabase signup data:', data);
      console.log('❌ Supabase signup error:', error);

      if (error) {
        console.error('❌ Supabase auth signup error:', error.message, error);

        if (error.message.includes('User already registered')) {
          throw new Error('An account with this email already exists. Please try logging in instead, or use a different email address.');
        }

        throw error;
      }

      const user = data.user;
      console.log('👤 Created user:', user);

      if (user) {
        console.log('💾 Attempting to insert user profile...');
        const { error: insertError } = await supabase.from('users').insert([
          { email, name, auth_id: user.id },
        ]);

        if (insertError) {
          console.error('❌ Failed to create user profile:', insertError.message, insertError.code);
          if (insertError.code === '23505') {
            console.log('✅ User profile already exists, continuing...');
          } else {
            throw new Error(`Failed to create user profile: ${insertError.message}`);
          }
        } else {
          console.log('✅ User profile created successfully');
        }
      }

      if (data.user && !data.session) {
        console.log('📨 Email confirmation required');
        throw new Error('Please check your email and click the confirmation link to complete registration.');
      }

      console.log('🎉 Registration completed successfully');
      return data;
    } catch (error) {
      console.error('❌ Registration error:', error.message);
      throw error;
    }
  };

  const login = async (email, password) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (error) throw error;

    const {
      data: { session },
      error: sessionError,
    } = await supabase.auth.getSession();

    if (sessionError || !session || !session.user) {
      console.warn('⚠️ Login session missing or delayed, skipping user setup for now.');
      return null;
    }

    const authUser = session.user;
    setToken(session.access_token); // 🔑 capture token after login

    const { data: userData, error: userError } = await supabase
      .from('users')
      .select('account_setup_complete')
      .eq('auth_id', authUser.id)
      .single();

    if (userError) {
      console.error('❌ Failed to fetch user profile:', userError.message);
    }

    const enrichedUser = {
      id: authUser.id,
      email: authUser.email,
      name: authUser.user_metadata?.name || '',
//      isSetupComplete: authUser.user_metadata?.isSetupComplete ?? false,
      accountSetupComplete: userData?.account_setup_complete ?? false,
    };

    setUser(enrichedUser);
    return enrichedUser;
  };

  const logout = async () => {
    await supabase.auth.signOut();
    setUser(null);
    setProfile(null);
    setToken(null);
  };

  const completeSetup = async () => { }; // extend as needed

  const checkUserStatus = async (email) => {
    try {
      console.log('🔍 Checking user status for:', email);
      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('email, auth_id')
        .eq('email', email)
        .single();

      console.log('👤 User data:', userData);
      console.log('❌ User error:', userError);

      if (userError && userError.code !== 'PGRST116') {
        console.error('Error checking user status:', userError.message, userError.code);
        return { exists: false, needsConfirmation: false };
      }

      const result = {
        exists: !!userData,
        needsConfirmation: userData && !userData.auth_id,
        userData: userData
      };

      console.log('📊 User status result:', result);
      return result;
    } catch (error) {
      console.error('Error in checkUserStatus:', error.message);
      return { exists: false, needsConfirmation: false };
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        profile,
        loading,
        login,
        register,
        logout,
        completeSetup,
        checkUserStatus,
        setUser,
        setProfile,
        token, // 🔑 expose JWT token
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
